import React from 'react';
import PropTypes from 'prop-types';
import './SkeletonLoader.css';

/**
 * Professional Skeleton Loader Component
 * 
 * Provides shimmering placeholder layouts while content is loading.
 * 
 * @param {string} type - 'text', 'title', 'avatar', 'thumbnail', 'button', 'card', 'table', 'list', 'form'
 * @param {number} count - Number of skeleton items to render
 * @param {string} className - Optional custom class
 * @param {object} style - Optional inline styles
 */
const SkeletonLoader = ({ type = 'text', count = 1, className = '', style = {} }) => {
    // Render a single basic element
    const renderItem = (elementType, idx) => (
        <div key={idx} className={`skeleton skeleton-${elementType} ${className}`} style={style}></div>
    );

    // High-level complex layouts
    const renderCard = (idx) => (
        <div key={idx} className={`skeleton-card ${className}`} style={style}>
            <div className="skeleton skeleton-thumbnail skeleton-card-img"></div>
            <div className="skeleton-card-body">
                <div className="skeleton skeleton-title"></div>
                <div className="skeleton skeleton-text"></div>
                <div className="skeleton skeleton-text w-75"></div>
            </div>
        </div>
    );

    const renderTableRow = (idx) => (
        <tr key={idx} className={`skeleton-table-row ${className}`} style={style}>
            <td><div className="skeleton skeleton-text"></div></td>
            <td><div className="skeleton skeleton-text"></div></td>
            <td><div className="skeleton skeleton-text w-75"></div></td>
            <td><div className="skeleton skeleton-text"></div></td>
            <td><div className="skeleton skeleton-button skeleton-sm"></div></td>
        </tr>
    );

    const renderListItem = (idx) => (
        <div key={idx} className={`skeleton-list-item ${className}`} style={style}>
            <div className="skeleton skeleton-avatar"></div>
            <div className="skeleton-list-content">
                <div className="skeleton skeleton-title"></div>
                <div className="skeleton skeleton-text w-50"></div>
            </div>
        </div>
    );

    const renderForm = (idx) => (
        <div key={idx} className={`skeleton-form ${className}`} style={style}>
            <div className="skeleton-form-group">
                <div className="skeleton skeleton-text w-25"></div>
                <div className="skeleton skeleton-input"></div>
            </div>
            <div className="skeleton-form-group">
                <div className="skeleton skeleton-text w-25"></div>
                <div className="skeleton skeleton-input"></div>
            </div>
            <div className="skeleton skeleton-button skeleton-form-btn"></div>
        </div>
    );

    /** Media rows skeleton (matches provided screenshot) */
    const renderMediaRow = (idx) => (
        <div key={idx} className={`skeleton-media-row ${className}`} style={style}>
            <div className="skeleton skeleton-media-thumb" />
            <div className="skeleton-media-main">
                <div className="skeleton skeleton-media-line skeleton-media-line-lg" />
                <div className="skeleton skeleton-media-line skeleton-media-line-md" />
                <div className="skeleton skeleton-media-line skeleton-media-line-sm" />
            </div>
            <div className="skeleton-media-side">
                <div className="skeleton skeleton-media-line skeleton-media-line-xs" />
                <div className="skeleton skeleton-media-line skeleton-media-line-xs" />
            </div>
        </div>
    );

    const renderMediaRows = () => (
        <div className={`skeleton-media-container ${className}`} style={style}>
            {Array.from({ length: Math.min(count, 8) }, (_, i) => renderMediaRow(i))}
        </div>
    );

    /** Full app shell: sidebar + top bar + stats + table (session restore) */
    const renderAppShell = () => (
        <div className={`skeleton-app-shell ${className}`} style={style}>
            <aside className="skeleton-app-shell-sidebar">
                <div className="skeleton skeleton-title w-75 mb-4" style={{ height: 24 }} />
                {Array.from({ length: 8 }, (_, i) => (
                    <div key={i} className="skeleton skeleton-text mb-3" style={{ width: `${70 + (i % 3) * 10}%` }} />
                ))}
            </aside>
            <div className="skeleton-app-shell-main">
                <header className="skeleton-app-shell-header">
                    <div className="skeleton skeleton-text w-50" style={{ height: 18 }} />
                    <div className="skeleton skeleton-avatar" style={{ width: 40, height: 40 }} />
                </header>
                <div className="skeleton-app-shell-breadcrumb">
                    <div className="skeleton skeleton-text w-33" style={{ height: 12 }} />
                </div>
                <div className="skeleton-app-shell-stats">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="skeleton-app-shell-stat-card">
                            <div className="skeleton skeleton-text w-50 mb-2" />
                            <div className="skeleton skeleton-title" style={{ width: '40%', height: 28 }} />
                        </div>
                    ))}
                </div>
                <div className="skeleton-app-shell-toolbar">
                    <div className="skeleton skeleton-input" style={{ maxWidth: 280, height: 38 }} />
                    <div className="skeleton skeleton-button skeleton-sm" />
                </div>
                <div className="skeleton-app-shell-table">
                    <div className="skeleton-app-shell-table-head">
                        {Array.from({ length: 6 }, (_, j) => (
                            <div key={j} className="skeleton skeleton-text" style={{ height: 14, flex: 1 }} />
                        ))}
                    </div>
                    {Array.from({ length: Math.min(count, 10) }, (_, r) => (
                        <div key={r} className="skeleton-app-shell-table-row">
                            {Array.from({ length: 6 }, (_, c) => (
                                <div key={c} className="skeleton skeleton-text" style={{ height: 12, flex: 1 }} />
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    // Main rendering logic
    const elements = [];
    for (let i = 0; i < count; i++) {
        switch (type) {
            case 'card':
                elements.push(renderCard(i));
                break;
            case 'table':
                elements.push(renderTableRow(i));
                break;
            case 'list':
                elements.push(renderListItem(i));
                break;
            case 'form':
                elements.push(renderForm(i));
                break;
            case 'mediaRows':
                return renderMediaRows();
            case 'appShell':
                return renderAppShell();
            default:
                // Basic types: text, title, avatar, thumbnail, button, input
                elements.push(renderItem(type, i));
                break;
        }
    }

    // If table rows, wrap in tbody structure (if not strictly inside a table already)
    if (type === 'table') {
        return (
            <div className="skeleton-table-container">
                <table className="skeleton-table">
                    <tbody>{elements}</tbody>
                </table>
            </div>
        );
    }

    return (
        <div className={`skeleton-wrapper skeleton-wrapper-${type}`}>
            {elements}
        </div>
    );
};

SkeletonLoader.propTypes = {
    type: PropTypes.oneOf(['text', 'title', 'avatar', 'thumbnail', 'button', 'input', 'card', 'table', 'list', 'form', 'mediaRows', 'appShell']),
    count: PropTypes.number,
    className: PropTypes.string,
    style: PropTypes.object,
};

export default SkeletonLoader;
